import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Input } from "semantic-ui-react";

import { ReactTable } from "components";
import "components/ROB/components/ROBSlaTable/ROBSlaTable.scss";
import { PAGE_SIZE } from "utils/common";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { InputOnChangeData } from "semantic-ui-react";

type ROBSlaTableProps<T> = {
  rows: T[];
  columns: AccessorColumnDef<T>[];
  columnVisibility?: Record<string, boolean>;
};

export const ROBSlaTable = <T extends Record<string, unknown>>({ rows, columns, columnVisibility }: ROBSlaTableProps<T>) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE.PageSize25 });

  const query = search.trim().toLowerCase();
  const filteredRows = query ? rows.filter(row => Object.values(row).join(" ").toLowerCase().includes(query)) : rows;
  const pagedRows = filteredRows.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize);

  const handleSearch = (_: unknown, { value }: InputOnChangeData) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const pageCount = Math.ceil(filteredRows.length / pagination.pageSize);

  return (
    <div className="ROBSlaTable">
      <Input icon={<Icon className="magnifying glass" />} iconPosition="left" placeholder={t("v8_search", "Search")} value={search} onChange={handleSearch} />

      <ReactTable
        columns={columns}
        data={pagedRows}
        emptyRowMessage={t("v8_no_data_available", "No data available")}
        state={columnVisibility && { columnVisibility }}
        pagination={pageCount > 1 ? pagination : undefined}
        pageCount={pageCount}
        totalItems={filteredRows.length}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
      />
    </div>
  );
};
